import React from "react";
import {
  Grid,
  Segment,
  Header,
  Icon,
  Table,
  Progress,
  Label,
  Loader,
  Tab,
} from "semantic-ui-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { StatCard } from "../../StatCard";
import "./FinancialTab.scss";

export function FinancialTab(props) {
  const { financialData } = props;

  if (!financialData) {
    return <Loader active>Cargando datos financieros...</Loader>;
  }

  const overview = financialData.overview || {};
  const collectionRate = overview.collectionRateOverall || 0;

  const paymentMethodLabels = {
    1: "Efectivo",
    2: "Yape",
    3: "Plin",
    4: "Transferencia",
    5: "Otros",
  };
  const paymentMethodColors = {
    1: "#10b981",
    2: "#8b5cf6",
    3: "#06b6d4",
    4: "#3b82f6",
    5: "#6b7280",
  };
  const paymentMethodClasses = {
    1: "efectivo",
    2: "yape",
    3: "plin",
    4: "transferencia",
    5: "otros",
  };

  return (
    <Tab.Pane>
      <Grid stackable>
        <Grid.Row columns={4}>
          <Grid.Column>
            <StatCard
              title="Ingresos Totales"
              value={
                overview.total?.amount
                  ? `S/ ${overview.total.amount.toLocaleString()}`
                  : "S/ 0"
              }
              icon="money bill alternate"
              color="#21ba45"
            />
          </Grid.Column>
          <Grid.Column>
            <StatCard
              title="Cobrado"
              value={
                overview.collected?.amount
                  ? `S/ ${overview.collected.amount.toLocaleString()}`
                  : "S/ 0"
              }
              icon="check circle"
              color="#00b5ad"
            />
          </Grid.Column>
          <Grid.Column>
            <StatCard
              title="Pendiente"
              value={
                overview.pending?.amount
                  ? `S/ ${overview.pending.amount.toLocaleString()}`
                  : "S/ 0"
              }
              icon="clock"
              color="#f2711c"
            />
          </Grid.Column>
          <Grid.Column>
            <StatCard
              title="Tasa de Cobro"
              value={`${collectionRate}%`}
              icon="percent"
              color="#a333c8"
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row columns={2}>
          <Grid.Column width={8}>
            <Segment>
              <Header as="h4">
                <Icon name="credit card" />
                Métodos de Pago
              </Header>
              {financialData.paymentMethods?.length > 0 ? (
                <>
                  {(() => {
                    const paymentMethodsPieData =
                      financialData.paymentMethods.map((pm) => ({
                        name:
                          paymentMethodLabels[pm.method] ||
                          `Método ${pm.method}`,
                        value: pm.total || 0,
                        count: pm.count || 0,
                        fill: paymentMethodColors[pm.method] || "#6b7280",
                        method: pm.method,
                        className: paymentMethodClasses[pm.method] || "otros",
                      }));

                    return (
                      <>
                        <div className="payment-methods-summary">
                          {paymentMethodsPieData.map((pm) => (
                            <div
                              key={pm.method}
                              className={`payment-method-item ${pm.className}`}
                            >
                              <span className="value">{pm.count}</span>
                              <span className="label">{pm.name}</span>
                              <span className="amount">
                                S/ {pm.value.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                        {paymentMethodsPieData.length > 0 && (
                          <div
                            style={{
                              width: "100%",
                              height: 200,
                              marginTop: "1rem",
                            }}
                          >
                            <ResponsiveContainer>
                              <PieChart>
                                <Pie
                                  data={paymentMethodsPieData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={50}
                                  outerRadius={80}
                                  paddingAngle={3}
                                  dataKey="value"
                                >
                                  {paymentMethodsPieData.map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={entry.fill}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip
                                  formatter={(value, name) => [
                                    `S/ ${value.toLocaleString()}`,
                                    name,
                                  ]}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <p>No hay datos de métodos de pago disponibles</p>
              )}
            </Segment>
          </Grid.Column>

          <Grid.Column width={8}>
            <Segment>
              <Header as="h4">
                <Icon name="percent" />
                Tasa de Cobranza
              </Header>
              <div style={{ textAlign: "center", padding: "1rem 0" }}>
                <div
                  style={{
                    fontSize: "3em",
                    fontWeight: "700",
                    color:
                      collectionRate > 80
                        ? "#10b981"
                        : collectionRate > 60
                        ? "#f59e0b"
                        : "#ef4444",
                    marginBottom: "0.5rem",
                  }}
                >
                  {collectionRate.toFixed(1)}%
                </div>
                <p style={{ color: "var(--text-color-70)", margin: 0 }}>
                  de pagos cobrados
                </p>
              </div>
              <Progress
                percent={collectionRate}
                color={
                  collectionRate > 80
                    ? "green"
                    : collectionRate > 60
                    ? "yellow"
                    : "red"
                }
                size="small"
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.9em",
                  color: "var(--text-color-70)",
                  marginTop: "0.5rem",
                }}
              >
                <span>Total: {overview.total?.count || 0} pagos</span>
                <span>S/ {(overview.total?.amount || 0).toLocaleString()}</span>
              </div>
            </Segment>
          </Grid.Column>
        </Grid.Row>

        {financialData.overduePayments?.payments?.length > 0 && (
          <Grid.Row>
            <Grid.Column>
              <Segment>
                <Header as="h4">
                  <Icon name="exclamation triangle" color="red" />
                  Pagos Vencidos ({financialData.overduePayments.count})
                </Header>
                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Estudiante</Table.HeaderCell>
                      <Table.HeaderCell>Concepto</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">
                        Monto
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">
                        Días Vencido
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {financialData.overduePayments.payments
                      .slice(0, 10)
                      .map((payment, index) => (
                        <Table.Row key={index}>
                          <Table.Cell>{payment.studentName}</Table.Cell>
                          <Table.Cell>{payment.conceptName}</Table.Cell>
                          <Table.Cell textAlign="right">
                            S/ {payment.amount.toLocaleString()}
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            <Label
                              color={
                                payment.daysOverdue > 60 ? "red" : "orange"
                              }
                            >
                              {payment.daysOverdue} días
                            </Label>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                  </Table.Body>
                </Table>
              </Segment>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </Tab.Pane>
  );
}
